import CheckboxFilterSection from '@c/filters/components/CheckboxFilterSection';
import RadioFilterSection from '@c/filters/components/RadioFilterSection';
import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import Toggle from '@ui/Toggle';
import { CONDITIONS } from '@util/createListingHelpers';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import { Pencil } from 'lucide-react';
import type { Condition } from 'models/product';
import type { SellerType } from 'models/shared';
import { useState } from 'react';
import VehicleLocationModal from './VehicleLocationModal';

// Seller type options shared between the sidebar and the mobile drawer.
export const SELLER_TYPE_OPTIONS: { key: SellerType; label: string }[] = [
  { key: 'individual', label: 'Individual' },
  { key: 'dealer', label: 'Dealer' },
];

// Price-range preset checkboxes. `maxPrice: 0` means no upper bound.
const PRICE_RANGE_PRESETS: {
  label: string;
  minPrice: number;
  maxPrice: number;
}[] = [
  { label: 'Under $1,000', minPrice: 0, maxPrice: 1000 },
  { label: '$1,000 - $5,000', minPrice: 1000, maxPrice: 5000 },
  { label: 'Over $5,000', minPrice: 5000, maxPrice: 0 },
];

interface VehicleFilterSidebarProps {
  sideFilters: PageSideFilters;
  onChange: (filters: PageSideFilters) => void;
  bikeMakes: string[];
}

// Desktop side panel filters: Location, Seller type, Condition,
// Manufacturer, Price.
const VehicleFilterSidebar = ({
  sideFilters,
  onChange,
  bikeMakes,
}: VehicleFilterSidebarProps) => {
  const [useMyLocation, setUseMyLocation] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  // A preset is active when both bounds match the current price filter.
  const isPresetSelected = (minPrice: number, maxPrice: number) =>
    (sideFilters.minPrice || 0) === minPrice &&
    (sideFilters.maxPrice || 0) === maxPrice;

  // Toggling a preset sets its bounds; toggling the active one clears them.
  const handlePresetToggle = (minPrice: number, maxPrice: number) => {
    if (isPresetSelected(minPrice, maxPrice)) {
      onChange({ ...sideFilters, minPrice: 0, maxPrice: 0 });
    } else {
      onChange({ ...sideFilters, minPrice, maxPrice });
    }
  };

  // "Use my location": ON geolocates the device, reverse-geocodes it and
  // sets the location filter; OFF clears it.
  const handleUseMyLocation = (enabled: boolean) => {
    if (!enabled) {
      setUseMyLocation(false);
      onChange({ ...sideFilters, location: null });
      return;
    }
    if (!navigator.geolocation) return;
    setUseMyLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch(
            `/api/geocode?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
          );
          const data = await res.json();
          if (data && typeof data.address === 'string' && data.address) {
            onChange({ ...sideFilters, location: data });
          } else {
            setUseMyLocation(false);
          }
        } catch {
          setUseMyLocation(false);
        }
      },
      () => setUseMyLocation(false)
    );
  };

  return (
    <div className="flex w-full min-w-[28.2rem] flex-col rounded-brand bg-white pb-4 pr-4 sm:max-w-[28.2rem]">
      {/* Location — static placeholder cards, wired up later */}
      <div className="border-b border-zinc-300">
        <Accordion title="Location" startOpen titleClassName="text-[1.8rem]">
          <div className="flex flex-col gap-[0.8rem] px-[0.8rem] pb-[1.2rem]">
            <button
              type="button"
              onClick={() => setLocationModalOpen(true)}
              className="flex w-full items-start gap-[0.8rem] rounded-[1.5rem] bg-[#F1F1F1] p-[2rem] text-left"
            >
              <div className="flex flex-1 flex-col">
                <span className="text-[1.6rem] font-semibold text-black">
                  {sideFilters.location?.address ?? 'Anywhere'}
                </span>
                <span className="text-[1.4rem] font-medium text-[#444444]">
                  {sideFilters.location
                    ? `Within ${sideFilters.radius ?? 50} mi`
                    : 'Tap to choose a location'}
                </span>
              </div>
              <Pencil className="h-[1.8rem] w-[1.8rem] shrink-0 text-black" />
            </button>
            <div className="flex flex-col gap-[0.4rem] rounded-[1.5rem] bg-[#F1F1F1] p-[2rem]">
              <div className="flex items-center justify-between gap-[0.8rem]">
                <span className="text-[1.6rem] font-semibold text-black">
                  Use my location
                </span>
                <Toggle enabled={useMyLocation} onChange={handleUseMyLocation} />
              </div>
              <span className="text-[1.4rem] font-medium text-[#444444]">
                Turn ON to find vehicle on your area
              </span>
            </div>
          </div>
        </Accordion>
      </div>
      <CheckboxFilterSection
        title="Seller type"
        startOpen
        filterOptions={SELLER_TYPE_OPTIONS.map((opt) => ({
          key: opt.key,
          label: opt.label,
          id: opt.key,
        }))}
        onChange={(filters) => {
          onChange({
            ...sideFilters,
            sellerType: filters as SellerType[],
          });
        }}
        filterState={sideFilters.sellerType ?? []}
      />
      <RadioFilterSection
        title="Condition"
        startOpen
        titleClassName="text-[1.8rem]"
        filterOptions={CONDITIONS.map((c) => ({
          key: c.replaceAll(' ', '-'),
          label: c,
        }))}
        onChange={(filters) => {
          onChange({
            ...sideFilters,
            bikeConditions: filters as Condition[],
          });
        }}
        initialFilters={sideFilters.bikeConditions as string[]}
      />
      <CheckboxFilterSection
        title="Manufacturer"
        startOpen
        filterOptions={bikeMakes.map((make) => ({
          key: make,
          label: make,
          id: make,
        }))}
        onChange={(filters) => {
          onChange({ ...sideFilters, bikeMakes: filters });
        }}
        filterState={sideFilters.bikeMakes ?? []}
      />
      <div className="border-b border-zinc-300">
        <Accordion title="Price" startOpen titleClassName="text-[1.8rem]">
          <div className="flex w-full flex-col gap-[1.2rem] px-[0.8rem] pb-[1.2rem]">
            <div className="flex flex-col gap-[0.8rem]">
              {PRICE_RANGE_PRESETS.map((preset) => (
                <Checkbox
                  key={preset.label}
                  label={preset.label}
                  selected={isPresetSelected(preset.minPrice, preset.maxPrice)}
                  onChange={() =>
                    handlePresetToggle(preset.minPrice, preset.maxPrice)
                  }
                />
              ))}
            </div>
            <div className="flex w-full items-center gap-[0.8rem]">
              <div className="flex grow items-center gap-[0.4rem] rounded-[1rem] bg-[#F1F1F1] px-[1rem] py-[0.8rem]">
                <span className="text-[1.4rem] font-bold text-brand-black">
                  USD
                </span>
                <input
                  type="number"
                  placeholder="Min"
                  value={sideFilters.minPrice || ''}
                  onChange={(e) => {
                    onChange({
                      ...sideFilters,
                      minPrice: e.target.value ? Number(e.target.value) : 0,
                    });
                  }}
                  className="w-full min-w-0 border-none bg-transparent text-[1.4rem] outline-none"
                />
              </div>
              <span className="text-[1.4rem] font-medium text-zinc-700">
                to
              </span>
              <div className="flex grow items-center gap-[0.4rem] rounded-[1rem] bg-[#F1F1F1] px-[1rem] py-[0.8rem]">
                <span className="text-[1.4rem] font-bold text-brand-black">
                  USD
                </span>
                <input
                  type="number"
                  placeholder="Max"
                  value={sideFilters.maxPrice || ''}
                  onChange={(e) => {
                    onChange({
                      ...sideFilters,
                      maxPrice: e.target.value ? Number(e.target.value) : 0,
                    });
                  }}
                  className="w-full min-w-0 border-none bg-transparent text-[1.4rem] outline-none"
                />
              </div>
            </div>
          </div>
        </Accordion>
      </div>

      <VehicleLocationModal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        location={sideFilters.location ?? null}
        radius={sideFilters.radius ?? null}
        onApply={(location, radius) => {
          onChange({ ...sideFilters, location, radius });
        }}
      />
    </div>
  );
};

export default VehicleFilterSidebar;
